import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { provinces, getDistricts } from '../data/turkey';
import { submitCourierApplication, submitRestaurantApplication, submitCompanyApplication, generateCaptcha } from '../lib/api';

const sortedProvinces = [...provinces].sort((a, b) => a.name.localeCompare(b.name, 'tr'));

// Başvuru başarısında reklam dönüşüm olayını tetikler. Analitik kurulu değilse
// hepsi sessizce atlanır (no-op). /bayilik'te yakalanan UTM'i de olaya ekler.
function basvuruTakip(formType) {
  let utm = {};
  try { utm = JSON.parse(localStorage.getItem('agros_utm') || '{}'); } catch { utm = {}; }
  try { window.dataLayer && window.dataLayer.push({ event: 'basvuru', form_type: formType, ...utm }); } catch { /* no-op */ }
  try { window.gtag && window.gtag('event', 'generate_lead', { form_type: formType, ...utm }); } catch { /* no-op */ }
  try { window.fbq && window.fbq('track', 'Lead', { content_name: formType }); } catch { /* no-op */ }
}

const FORM_TITLES = {
  courier: { tr: 'Kurye Başvurusu', en: 'Courier Application' },
  restaurant: { tr: 'Restoran Başvurusu', en: 'Restaurant Application' },
  company: { tr: 'Kurye Şirketi Başvurusu', en: 'Courier Company Application' },
};

export const ApplicationModal = ({ isOpen, onClose, defaultForm = 'courier' }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [submitResult, setSubmitResult] = useState(null); // { type: 'success' | 'error', message: string }

  const handlePhoneChange = (value, setter, form) => {
    // Only allow digits
    const digits = value.replace(/\D/g, '');
    // Max 11 digits
    const trimmed = digits.slice(0, 11);
    setter({ ...form, phone: trimmed });
    
    if (trimmed.length > 0 && !trimmed.startsWith('05')) {
      setPhoneError('Telefon numarası 05 ile başlamalıdır');
    } else if (trimmed.length > 0 && trimmed.length < 11) {
      setPhoneError('Telefon numarası 11 haneli olmalıdır');
    } else {
      setPhoneError('');
    }
  };

  const isPhoneValid = (phone) => phone.startsWith('05') && phone.length === 11;

  const [courierForm, setCourierForm] = useState({
    full_name: '', phone: '', birth_day: '', birth_month: '', birth_year: '',
    province: '', district: '', license_types: [],
    has_motorcycle: false, motorcycle_brand: '', motorcycle_model: '',
    daily_hours: '', experience: '', description: '',
  });

  const [restaurantForm, setRestaurantForm] = useState({
    restaurant_name: '', contact_name: '', phone: '', province: '', district: '',
    address: '', package_count: '', has_courier: false, uses_other_service: false,
    visit_date: '', visit_time_slot: '',
  });

  const [companyForm, setCompanyForm] = useState({
    full_name: '', phone: '', province: '', district: '',
    has_active_company: false, package_count: '', application_type: '',
  });

  // Güvenlik sorusu tek kullanımlık: pencere açılınca ve her gönderimden sonra yenisi alınır.
  // `tuzak`: ekranda görünmeyen alan — insan boş bırakır, form dolduran robot doldurur.
  const [guvenlik, setGuvenlik] = useState({ id: '', soru: '', cevap: '' });
  const [tuzak, setTuzak] = useState('');
  const yeniSoru = async () => {
    try {
      const { data } = await generateCaptcha();
      setGuvenlik({ id: data.captcha_id, soru: data.question, cevap: '' });
    } catch {
      setGuvenlik({ id: '', soru: '', cevap: '' });
    }
  };
  const guvenlikAlanlari = () => ({
    captcha_id: guvenlik.id,
    captcha_answer: guvenlik.cevap === '' ? null : parseInt(guvenlik.cevap, 10),
    website: tuzak,
  });

  useEffect(() => {
    if (isOpen) {
      setSubmitResult(null);
      yeniSoru();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const guvenlikBolumu = (onEk) => (
    <>
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor={`${onEk}-website`}>Web sitesi</label>
        <input id={`${onEk}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" value={tuzak} onChange={(e) => setTuzak(e.target.value)} />
      </div>
      <div>
        <Label htmlFor={`${onEk}-captcha`} className="form-v2-label">
          {t('captcha_label')}: {guvenlik.soru || '…'} *
        </Label>
        <Input
          id={`${onEk}-captcha`} inputMode="numeric" required value={guvenlik.cevap}
          onChange={(e) => setGuvenlik({ ...guvenlik, cevap: e.target.value.replace(/\D/g, '').slice(0, 3) })}
          placeholder={t('captcha_placeholder')} className="form-v2-input mt-1.5" data-testid={`${onEk}-captcha-input`}
        />
      </div>
    </>
  );

  const handleCourierSubmit = async (e) => {
    e.preventDefault();
    if (!isPhoneValid(courierForm.phone)) { toast.error('Geçerli bir telefon numarası giriniz (05XXXXXXXXX)'); return; }
    // 18 yaş kontrolü
    if (!courierForm.birth_day || !courierForm.birth_month || !courierForm.birth_year) {
      toast.error('Doğum tarihi zorunludur'); return;
    }
    const birthDate = new Date(parseInt(courierForm.birth_year), parseInt(courierForm.birth_month) - 1, parseInt(courierForm.birth_day));
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    if (age < 18) { toast.error('Başvuru yapabilmek için 18 yaşından büyük olmanız gerekmektedir'); return; }
    setLoading(true);
    try {
      await submitCourierApplication({
        ...courierForm,
        birth_day: parseInt(courierForm.birth_day),
        birth_month: parseInt(courierForm.birth_month),
        birth_year: parseInt(courierForm.birth_year),
        ...guvenlikAlanlari(),
      });
      basvuruTakip('courier');
      setSubmitResult({ type: 'success', message: 'Kurye başvurunuz başarıyla alındı. En kısa sürede sizinle iletişime geçeceğiz.' });
      setCourierForm({ full_name: '', phone: '', birth_day: '', birth_month: '', birth_year: '', province: '', district: '', license_types: [], has_motorcycle: false, motorcycle_brand: '', motorcycle_model: '', daily_hours: '', experience: '', description: '' });
    } catch (err) {
      setSubmitResult({ type: 'error', message: err.response?.data?.detail || 'Başvuru gönderilemedi. Lütfen tekrar deneyin.' });
    }
    setLoading(false);
    yeniSoru();
  };

  const handleRestaurantSubmit = async (e) => {
    e.preventDefault();
    if (!isPhoneValid(restaurantForm.phone)) { toast.error('Geçerli bir telefon numarası giriniz (05XXXXXXXXX)'); return; }
    setLoading(true);
    try {
      await submitRestaurantApplication({ ...restaurantForm, ...guvenlikAlanlari() });
      basvuruTakip('restaurant');
      setSubmitResult({ type: 'success', message: 'Restoran başvurunuz başarıyla alındı. En kısa sürede sizinle iletişime geçeceğiz.' });
      setRestaurantForm({ restaurant_name: '', contact_name: '', phone: '', province: '', district: '', address: '', package_count: '', has_courier: false, uses_other_service: false, visit_date: '', visit_time_slot: '' });
    } catch (err) {
      setSubmitResult({ type: 'error', message: err.response?.data?.detail || 'Başvuru gönderilemedi. Lütfen tekrar deneyin.' });
    }
    setLoading(false);
    yeniSoru();
  };

  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    if (!isPhoneValid(companyForm.phone)) { toast.error('Geçerli bir telefon numarası giriniz (05XXXXXXXXX)'); return; }
    setLoading(true);
    try {
      await submitCompanyApplication({ ...companyForm, ...guvenlikAlanlari() });
      basvuruTakip('company');
      setSubmitResult({ type: 'success', message: 'Şirket başvurunuz başarıyla alındı. En kısa sürede sizinle iletişime geçeceğiz.' });
      setCompanyForm({ full_name: '', phone: '', province: '', district: '', has_active_company: false, package_count: '', application_type: '' });
    } catch (err) {
      setSubmitResult({ type: 'error', message: err.response?.data?.detail || 'Başvuru gönderilemedi. Lütfen tekrar deneyin.' });
    }
    setLoading(false);
    yeniSoru();
  };

  const licenseOptions = ['A', 'A1', 'A2', 'B'];
  const hourOptions = [
    { value: '4-6', label: t('hours_4_6') },
    { value: '6-10', label: t('hours_6_10') },
    { value: '10-12', label: t('hours_10_12') },
    { value: '12+', label: t('hours_12_plus') },
  ];

  const formTitle = FORM_TITLES[defaultForm]?.[i18n.language] || FORM_TITLES[defaultForm]?.tr || '';

  const timeSlots = [
    '11:00 - 12:00', '12:00 - 13:00', '13:00 - 14:00', '14:00 - 15:00',
    '15:00 - 16:00', '16:00 - 17:00', '17:00 - 18:00', '18:00 - 19:00',
    '19:00 - 20:00', '20:00 - 21:00', '21:00 - 22:00', '22:00 - 23:00',
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        data-testid="application-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full sm:max-w-2xl flex flex-col bg-background sm:rounded-3xl shadow-2xl border-0 sm:border sm:border-border sm:max-h-[92vh] sm:my-auto overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          data-testid="application-modal"
        >
          {/* Header - always visible */}
          <div className="form-v2-modal-header flex-shrink-0 flex items-center justify-between p-5 sm:p-7">
            <div className="min-w-0 pr-3">
              <h2 className="font-heading font-bold text-xl sm:text-2xl truncate text-foreground" data-testid="modal-title">{formTitle}</h2>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{t('apply_subtitle')}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-full border-1.5 border-border bg-background/60 backdrop-blur hover:bg-foreground/10 hover:border-foreground/30 transition-all flex items-center justify-center"
              aria-label="Close"
              data-testid="modal-close-btn"
            >
              <X className="w-5 h-5 text-foreground" />
            </button>
          </div>

          {/* Form Content - scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain p-5 sm:p-7">
            {submitResult ? (
              <div className="flex flex-col items-center text-center py-10 sm:py-12 space-y-6" data-testid="submit-result">
                {submitResult.type === 'success' ? (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400/20 to-emerald-500/20 ring-4 ring-green-500/15 flex items-center justify-center">
                    <CheckCircle2 className="w-12 h-12 text-green-500" />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-400/20 to-rose-500/20 ring-4 ring-red-500/15 flex items-center justify-center">
                    <AlertCircle className="w-12 h-12 text-red-500" />
                  </div>
                )}
                <div className="space-y-2">
                  <h3 className="font-heading font-bold text-2xl text-foreground">
                    {submitResult.type === 'success' ? 'Başvurunuz Alındı!' : 'Bir Sorun Oluştu'}
                  </h3>
                  <p className="text-muted-foreground text-sm sm:text-base max-w-sm">{submitResult.message}</p>
                </div>
                <div className="w-full max-w-xs">
                  {submitResult.type === 'success' ? (
                    <button
                      type="button"
                      onClick={() => { setSubmitResult(null); onClose(); }}
                      className="form-v2-submit w-full"
                      data-testid="result-go-home-btn"
                    >
                      Anasayfaya Dön
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSubmitResult(null)}
                      className="form-v2-submit w-full"
                      data-testid="result-retry-btn"
                    >
                      Tekrar Dene
                    </button>
                  )}
                </div>
              </div>
            ) : (
            <>
            {defaultForm === 'courier' && (
              <form onSubmit={handleCourierSubmit} className="space-y-3 sm:space-y-4" data-testid="courier-form-content">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="courier-name" className="form-v2-label">{t('full_name')} *</Label>
                    <Input id="courier-name" value={courierForm.full_name} onChange={(e) => setCourierForm({ ...courierForm, full_name: e.target.value })} required className="form-v2-input mt-1.5" data-testid="courier-fullname-input" />
                  </div>
                  <div>
                    <Label htmlFor="courier-phone" className="form-v2-label">{t('phone')} *</Label>
                    <Input id="courier-phone" type="tel" value={courierForm.phone} onChange={(e) => handlePhoneChange(e.target.value, setCourierForm, courierForm)} placeholder="05XXXXXXXXX" maxLength={11} required className="form-v2-input mt-1.5" data-testid="courier-phone-input" />
                    {phoneError && courierForm.phone.length > 0 && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
                  </div>
                </div>

                <div>
                  <Label className="form-v2-label">Doğum Tarihi *</Label>
                  <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-1">
                    <Select value={courierForm.birth_day} onValueChange={(v) => setCourierForm({ ...courierForm, birth_day: v })}>
                      <SelectTrigger className="form-v2-trigger" data-testid="courier-birth-day"><SelectValue placeholder="Gün" /></SelectTrigger>
                      <SelectContent>{Array.from({ length: 31 }, (_, i) => i + 1).map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={courierForm.birth_month} onValueChange={(v) => setCourierForm({ ...courierForm, birth_month: v })}>
                      <SelectTrigger className="form-v2-trigger" data-testid="courier-birth-month"><SelectValue placeholder="Ay" /></SelectTrigger>
                      <SelectContent>{['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'].map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={courierForm.birth_year} onValueChange={(v) => setCourierForm({ ...courierForm, birth_year: v })}>
                      <SelectTrigger className="form-v2-trigger" data-testid="courier-birth-year"><SelectValue placeholder="Yıl" /></SelectTrigger>
                      <SelectContent>{Array.from({ length: 60 }, (_, i) => new Date().getFullYear() - 18 - i).map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="form-v2-label">{t('province')} *</Label>
                    <Select value={courierForm.province} onValueChange={(v) => setCourierForm({ ...courierForm, province: v, district: '' })}>
                      <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="courier-province-select"><SelectValue placeholder={t('select')} /></SelectTrigger>
                      <SelectContent>{provinces.map((p) => (<SelectItem key={p.code} value={p.name}>{p.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-v2-label">{t('district')} *</Label>
                    <Select value={courierForm.district} onValueChange={(v) => setCourierForm({ ...courierForm, district: v })} disabled={!courierForm.province}>
                      <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="courier-district-select"><SelectValue placeholder={t('select')} /></SelectTrigger>
                      <SelectContent>{courierForm.province && getDistricts(provinces.find(p => p.name === courierForm.province)?.code).map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="form-v2-label">{t('license_types')} *</Label>
                  <div className="flex flex-wrap gap-2 sm:gap-3 mt-1.5">
                    {licenseOptions.map((license) => {
                      const active = courierForm.license_types.includes(license);
                      return (
                        <button
                          type="button"
                          key={license}
                          onClick={() => {
                            setCourierForm({
                              ...courierForm,
                              license_types: active
                                ? courierForm.license_types.filter(l => l !== license)
                                : [...courierForm.license_types, license],
                            });
                          }}
                          className="form-v2-pill"
                          data-active={active}
                          data-testid={`courier-license-${license}`}
                        >
                          {license}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <label
                  htmlFor="has-motorcycle"
                  className="form-v2-checkbox-card"
                  data-checked={courierForm.has_motorcycle}
                >
                  <Checkbox id="has-motorcycle" checked={courierForm.has_motorcycle} onCheckedChange={(checked) => setCourierForm({ ...courierForm, has_motorcycle: checked })} className="mt-0.5" data-testid="courier-has-motorcycle" />
                  <span className="text-sm sm:text-[0.9375rem] font-medium leading-snug flex-1">{t('has_motorcycle')}</span>
                </label>

                {courierForm.has_motorcycle && (
                  <div>
                    <Label htmlFor="motorcycle-brand" className="form-v2-label">{t('motorcycle_brand')}</Label>
                    <Input id="motorcycle-brand" value={courierForm.motorcycle_brand} onChange={(e) => setCourierForm({ ...courierForm, motorcycle_brand: e.target.value })} placeholder="Örn: Honda PCX 125" className="form-v2-input mt-1.5" data-testid="courier-motorcycle-brand" />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="form-v2-label">{t('daily_hours')} *</Label>
                    <Select value={courierForm.daily_hours} onValueChange={(v) => setCourierForm({ ...courierForm, daily_hours: v })}>
                      <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="courier-hours-select"><SelectValue placeholder={t('select')} /></SelectTrigger>
                      <SelectContent>{hourOptions.map((h) => (<SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="courier-experience" className="form-v2-label">{t('experience')}</Label>
                    <Input id="courier-experience" value={courierForm.experience} onChange={(e) => setCourierForm({ ...courierForm, experience: e.target.value })} className="form-v2-input mt-1.5" data-testid="courier-experience-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="courier-description" className="form-v2-label">{t('description')} *</Label>
                  <Textarea id="courier-description" value={courierForm.description} onChange={(e) => setCourierForm({ ...courierForm, description: e.target.value })} rows={2} required className="form-v2-textarea mt-1.5" data-testid="courier-description-input" />
                </div>

                {guvenlikBolumu('courier')}

                <button type="submit" className="form-v2-submit w-full" disabled={loading} data-testid="courier-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {loading ? t('submitting') : t('submit')}
                </button>
              </form>
            )}

            {defaultForm === 'restaurant' && (
              <form onSubmit={handleRestaurantSubmit} className="space-y-3 sm:space-y-4" data-testid="restaurant-form-content">
                <div>
                  <Label htmlFor="restaurant-name" className="form-v2-label">Restoran Adı *</Label>
                  <Input id="restaurant-name" value={restaurantForm.restaurant_name} onChange={(e) => setRestaurantForm({ ...restaurantForm, restaurant_name: e.target.value })} required className="form-v2-input mt-1.5" data-testid="restaurant-name-input" />
                </div>

                <div>
                  <Label htmlFor="contact-name" className="form-v2-label">Yetkili Ad Soyad *</Label>
                  <Input id="contact-name" value={restaurantForm.contact_name} onChange={(e) => setRestaurantForm({ ...restaurantForm, contact_name: e.target.value })} required className="form-v2-input mt-1.5" data-testid="restaurant-contact-input" />
                </div>

                <div>
                  <Label htmlFor="restaurant-phone" className="form-v2-label">Telefon *</Label>
                  <Input id="restaurant-phone" type="tel" value={restaurantForm.phone} onChange={(e) => handlePhoneChange(e.target.value, setRestaurantForm, restaurantForm)} placeholder="05XXXXXXXXX" maxLength={11} required className="form-v2-input mt-1.5" data-testid="restaurant-phone-input" />
                  {phoneError && restaurantForm.phone.length > 0 && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="form-v2-label">İl *</Label>
                    <Select value={restaurantForm.province} onValueChange={(v) => setRestaurantForm({ ...restaurantForm, province: v, district: '' })}>
                      <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="restaurant-province-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>{provinces.map((p) => (<SelectItem key={p.code} value={p.name}>{p.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-v2-label">İlçe *</Label>
                    <Select value={restaurantForm.district} onValueChange={(v) => setRestaurantForm({ ...restaurantForm, district: v })} disabled={!restaurantForm.province}>
                      <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="restaurant-district-select"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                      <SelectContent>{restaurantForm.province && getDistricts(provinces.find(p => p.name === restaurantForm.province)?.code).map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="restaurant-address" className="form-v2-label">Adres *</Label>
                  <Textarea id="restaurant-address" value={restaurantForm.address} onChange={(e) => setRestaurantForm({ ...restaurantForm, address: e.target.value })} rows={2} required className="form-v2-textarea mt-1.5" data-testid="restaurant-address-input" />
                </div>

                <div>
                  <Label htmlFor="package-count" className="form-v2-label">Günlük Tahmini Paket Sayınız? *</Label>
                  <Input id="package-count" value={restaurantForm.package_count} onChange={(e) => setRestaurantForm({ ...restaurantForm, package_count: e.target.value })} placeholder="Örn: 0-15, 15-35, 50-100" required className="form-v2-input mt-1.5" data-testid="restaurant-package-input" />
                </div>

                <div className="space-y-3">
                  <label htmlFor="has-courier" className="form-v2-checkbox-card" data-checked={restaurantForm.has_courier}>
                    <Checkbox id="has-courier" checked={restaurantForm.has_courier} onCheckedChange={(checked) => setRestaurantForm({ ...restaurantForm, has_courier: checked })} className="mt-0.5" data-testid="restaurant-has-courier" />
                    <span className="text-sm sm:text-[0.9375rem] font-medium leading-snug flex-1">Mevcutta kendi bünyenizde kuryeniz var mı?</span>
                  </label>
                  <label htmlFor="uses-other" className="form-v2-checkbox-card" data-checked={restaurantForm.uses_other_service}>
                    <Checkbox id="uses-other" checked={restaurantForm.uses_other_service} onCheckedChange={(checked) => setRestaurantForm({ ...restaurantForm, uses_other_service: checked })} className="mt-0.5" data-testid="restaurant-uses-other" />
                    <span className="text-sm sm:text-[0.9375rem] font-medium leading-snug flex-1">Başka bir platform ya da şirketten kurye hizmeti alıyor musunuz?</span>
                  </label>
                </div>

                {/* Visit Date & Time */}
                <div>
                  <Label className="form-v2-label">Sizi ne zaman ziyaret edelim? *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-2">
                    <div>
                      <Label htmlFor="visit-date" className="form-v2-label-sm">Tarih</Label>
                      <Input
                        id="visit-date"
                        type="date"
                        value={restaurantForm.visit_date}
                        onChange={(e) => setRestaurantForm({ ...restaurantForm, visit_date: e.target.value })}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        className="form-v2-input mt-1.5"
                        data-testid="restaurant-visit-date"
                      />
                    </div>
                    <div>
                      <Label className="form-v2-label-sm">Saat Aralığı</Label>
                      <Select value={restaurantForm.visit_time_slot} onValueChange={(v) => setRestaurantForm({ ...restaurantForm, visit_time_slot: v })}>
                        <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="restaurant-visit-time"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {guvenlikBolumu('restaurant')}

                <button type="submit" className="form-v2-submit w-full" disabled={loading} data-testid="restaurant-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {loading ? t('submitting') : t('submit')}
                </button>
              </form>
            )}

            {defaultForm === 'company' && (
              <form onSubmit={handleCompanySubmit} className="space-y-3 sm:space-y-4" data-testid="company-form-content">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="company-name" className="form-v2-label">{t('full_name')} *</Label>
                    <Input id="company-name" value={companyForm.full_name} onChange={(e) => setCompanyForm({ ...companyForm, full_name: e.target.value })} required className="form-v2-input mt-1.5" data-testid="company-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="company-phone" className="form-v2-label">{t('phone')} *</Label>
                    <Input id="company-phone" type="tel" value={companyForm.phone} onChange={(e) => handlePhoneChange(e.target.value, setCompanyForm, companyForm)} placeholder="05XXXXXXXXX" maxLength={11} required className="form-v2-input mt-1.5" data-testid="company-phone-input" />
                    {phoneError && companyForm.phone.length > 0 && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label className="form-v2-label">{t('province')} *</Label>
                    <Select value={companyForm.province} onValueChange={(v) => setCompanyForm({ ...companyForm, province: v, district: '' })}>
                      <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="company-province-select"><SelectValue placeholder={t('select')} /></SelectTrigger>
                      <SelectContent>{sortedProvinces.map((p) => (<SelectItem key={p.code} value={p.name}>{p.name}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-v2-label">{t('district')} *</Label>
                    <Select value={companyForm.district} onValueChange={(v) => setCompanyForm({ ...companyForm, district: v })} disabled={!companyForm.province}>
                      <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="company-district-select"><SelectValue placeholder={t('select')} /></SelectTrigger>
                      <SelectContent>{companyForm.province && getDistricts(provinces.find(p => p.name === companyForm.province)?.code).map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}</SelectContent>
                    </Select>
                  </div>
                </div>

                <label htmlFor="has-active-company" className="form-v2-checkbox-card" data-checked={companyForm.has_active_company}>
                  <Checkbox id="has-active-company" checked={companyForm.has_active_company} onCheckedChange={(checked) => setCompanyForm({ ...companyForm, has_active_company: checked })} className="mt-0.5" data-testid="company-has-active" />
                  <span className="text-sm sm:text-[0.9375rem] font-medium leading-snug flex-1">{t('has_active_company')}</span>
                </label>

                {companyForm.has_active_company && (
                  <div>
                    <Label htmlFor="company-package" className="form-v2-label">{t('package_count')}</Label>
                    <Input id="company-package" value={companyForm.package_count} onChange={(e) => setCompanyForm({ ...companyForm, package_count: e.target.value })} placeholder="Örn: 100-200" className="form-v2-input mt-1.5" data-testid="company-package-input" />
                  </div>
                )}

                <div>
                  <Label className="form-v2-label">{t('application_type')} *</Label>
                  <Select value={companyForm.application_type} onValueChange={(v) => setCompanyForm({ ...companyForm, application_type: v })}>
                    <SelectTrigger className="form-v2-trigger mt-1.5" data-testid="company-type-select"><SelectValue placeholder={t('select')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="franchise_software">{t('franchise_software')}</SelectItem>
                      <SelectItem value="software_only">{t('software_only')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {guvenlikBolumu('company')}

                <button type="submit" className="form-v2-submit w-full" disabled={loading} data-testid="company-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {loading ? t('submitting') : t('submit')}
                </button>
              </form>
            )}
            </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
